import React, { useEffect, useState } from 'react';
import { Star, Loader2, MessageSquareQuote, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { testimonialsApi, formatApiError } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

/**
 * Self-service testimonials page accessible to all logged-in roles.
 */
export default function MyTestimonialPage() {
    const { t } = useTranslation();
    const { isAdmin } = useAuth();
    const [eligibility, setEligibility] = useState(null);
    const [mine, setMine] = useState(null);
    const [loading, setLoading] = useState(true);
    const [content, setContent] = useState('');
    const [rating, setRating] = useState(5);
    const [title, setTitle] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [eRes, mRes] = await Promise.all([
                testimonialsApi.eligibility(),
                testimonialsApi.me(),
            ]);
            setEligibility(eRes.data);
            setMine(mRes.data?.testimonial || null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (content.trim().length < 20) {
            toast.error(t('testimonials_page.min_length_error'));
            return;
        }
        setSubmitting(true);
        try {
            await testimonialsApi.submit({
                content: content.trim(),
                rating,
                author_title: title || undefined,
            });
            toast.success(t('testimonials_page.submit_success'));
            setContent('');
            setTitle('');
            setRating(5);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-[#9CA3AF]" />
            </div>
        );
    }

    const missingJobs = Math.max(0, (eligibility?.min_jobs || 0) - (eligibility?.jobs_count || 0));
    const missingAnalyzed = Math.max(0, (eligibility?.min_analyzed || 0) - (eligibility?.analyzed_count || 0));
    const reasonLabel = {
        not_enough_jobs: t('testimonials_page.reason_not_enough_jobs', { count: missingJobs }),
        not_enough_analyzed: t('testimonials_page.reason_not_enough_analyzed', { count: missingAnalyzed }),
        already_submitted: t('testimonials_page.reason_already_submitted'),
        no_org: t('testimonials_page.reason_no_org'),
    };

    const jobsPct = Math.min(100, ((eligibility?.jobs_count || 0) / (eligibility?.min_jobs || 1)) * 100);
    const analyzedPct = Math.min(100, ((eligibility?.analyzed_count || 0) / (eligibility?.min_analyzed || 1)) * 100);
    const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';

    return (
        <div className="space-y-6 max-w-3xl" data-testid="my-testimonial-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">{t('testimonials_page.page_title')}</h1>
                    <p className="text-sm text-[#9CA3AF] mt-1">
                        {t('testimonials_page.page_subtitle')}
                    </p>
                </div>
                {isAdmin && (
                    <Link to="/dashboard/admin/testimonials">
                        <Button variant="outline" size="sm" data-testid="goto-moderation-btn">
                            <Shield className="w-4 h-4 mr-2" />
                            {t('testimonials_page.goto_moderation')}
                        </Button>
                    </Link>
                )}
            </div>

            {/* My existing review */}
            {mine && (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid="my-existing-review">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                            <MessageSquareQuote className="w-5 h-5 text-[#002FA7]" />
                            <h3 className="font-semibold text-[#0A0A0A]">{t('testimonials_page.my_review')}</h3>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            mine.published ? 'bg-[#D1FAE5] text-[#059669]' : 'bg-[#FEF3C7] text-[#D97706]'
                        }`} data-testid="my-review-status">
                            {mine.published ? t('testimonials_page.status_published_home') : t('testimonials_page.status_pending_moderation')}
                        </span>
                    </div>
                    <div className="flex items-center gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                            <Star
                                key={i}
                                className={`w-4 h-4 ${i <= mine.rating ? 'fill-[#FFCC00] text-[#FFCC00]' : 'text-[#E5E7EB]'}`}
                            />
                        ))}
                    </div>
                    <p className="text-[#0A0A0A] whitespace-pre-line">{mine.content}</p>
                    {mine.author_title && (
                        <p className="text-xs text-[#9CA3AF] mt-3">{mine.author_title}</p>
                    )}
                    <p className="text-xs text-[#9CA3AF] mt-2">
                        {t('testimonials_page.submitted_on', { date: new Date(mine.created_at).toLocaleDateString(locale) })}
                    </p>
                </div>
            )}

            {/* Eligibility progress */}
            {!mine && (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-5">
                    <h3 className="font-semibold text-[#0A0A0A] mb-3">{t('testimonials_page.eligibility_title')}</h3>
                    <p className="text-sm text-[#4B5563] mb-4">
                        {t('testimonials_page.eligibility_description')}
                    </p>

                    <div className="space-y-4">
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-[#4B5563]">
                                    {t('testimonials_page.jobs_created')}
                                </span>
                                <span className="font-medium text-[#0A0A0A]">
                                    {eligibility?.jobs_count || 0} / {eligibility?.min_jobs}
                                </span>
                            </div>
                            <div className="w-full bg-[#F3F4F6] rounded-full h-2">
                                <div className="bg-[#002FA7] h-2 rounded-full" style={{ width: `${jobsPct}%` }} />
                            </div>
                        </div>

                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-[#4B5563]">
                                    {t('testimonials_page.cvs_analyzed')}
                                </span>
                                <span className="font-medium text-[#0A0A0A]">
                                    {eligibility?.analyzed_count || 0} / {eligibility?.min_analyzed}
                                </span>
                            </div>
                            <div className="w-full bg-[#F3F4F6] rounded-full h-2">
                                <div className="bg-[#002FA7] h-2 rounded-full" style={{ width: `${analyzedPct}%` }} />
                            </div>
                        </div>
                    </div>

                    {!eligibility?.eligible && eligibility?.reason && (
                        <div className="mt-4 flex items-start gap-2 text-sm text-[#92400E] bg-[#FEF3C7] border border-[#F59E0B]/30 rounded-md p-3" data-testid="eligibility-reason">
                            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>{reasonLabel[eligibility.reason] || t('testimonials_page.reason_default')}</span>
                        </div>
                    )}

                    {eligibility?.eligible && (
                        <div className="mt-4 flex items-start gap-2 text-sm text-[#065F46] bg-[#D1FAE5] border border-[#10B981]/30 rounded-md p-3" data-testid="eligibility-ok">
                            <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>{t('testimonials_page.eligibility_ok')}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Submit form (only when eligible AND no existing review) */}
            {!mine && eligibility?.eligible && (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 space-y-4" data-testid="my-submit-form">
                    <h3 className="font-semibold text-[#0A0A0A]">{t('testimonials_page.leave_review_title')}</h3>

                    <div>
                        <label className="text-sm text-[#4B5563]">{t('testimonials_page.rating_label')}</label>
                        <div className="flex items-center gap-1 mt-1" data-testid="my-rating">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setRating(i)}
                                    aria-label={i === 1 ? t('testimonials_page.star_label_one', { count: i }) : t('testimonials_page.star_label_other', { count: i })}
                                    data-testid={`my-star-${i}`}
                                >
                                    <Star
                                        className={`w-7 h-7 transition-colors ${
                                            i <= rating ? 'fill-[#FFCC00] text-[#FFCC00]' : 'text-[#E5E7EB]'
                                        }`}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="text-sm text-[#4B5563]">{t('testimonials_page.your_title_label')}</label>
                        <Input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={t('testimonials_page.your_title_placeholder')}
                            maxLength={120}
                            data-testid="my-title-input"
                        />
                    </div>

                    <div>
                        <label className="text-sm text-[#4B5563]">{t('testimonials_page.review_label')}</label>
                        <Textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder={t('testimonials_page.review_placeholder')}
                            rows={5}
                            maxLength={1000}
                            data-testid="my-content-input"
                        />
                        <p className="text-xs text-[#9CA3AF] mt-1">{content.length} / 1000</p>
                    </div>

                    <div className="flex justify-end">
                        <Button
                            onClick={submit}
                            disabled={submitting}
                            className="bg-[#002FA7] hover:bg-[#002480] text-white"
                            data-testid="my-submit-btn"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t('testimonials_page.sending')}
                                </>
                            ) : t('testimonials_page.submit_btn')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
